import { useAuth } from '@clerk/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { baseQueryWithAuth } from '@/store/base-query';
import type { CluChatMessage } from './admin-clu-chats.feature';

/** PostHog flag, keyed by organization, for sharing a Clu chat with the
 *  organization. The API reads the same key. Undefined while loading means
 *  off. */
export const CLU_CHAT_SHARING_FLAG = 'clu_chat_sharing_v1';

export function useCluChatSharingEnabled(): boolean {
  return useFeatureFlagEnabled(CLU_CHAT_SHARING_FLAG) === true;
}

/** PostHog flag, keyed by organization, for chatting in a teammate's shared
 *  chat as yourself (the API reads the same key). Off, a shared chat opens
 *  read only and Continue in my chat copies it. Undefined while loading means
 *  off. */
export const CLU_MULTIPLAYER_FLAG = 'clu_multiplayer_chats_v1';

export function useCluMultiplayerEnabled(): boolean {
  return useFeatureFlagEnabled(CLU_MULTIPLAYER_FLAG) === true;
}

/** A chat as a teammate reads it: each message's text and the tools Clu
 *  called, never the tools' results (the staff review's shape). */
export interface SharedCluChat {
  id: string;
  title: string | null;
  owner_user_id: string;
  is_owner: boolean;
  /** The agent whose chat it is, where it opens; null for a Clu chat. */
  context_agent_id?: string | null;
  shared_at: string | null;
  modified_at: string;
  messages: CluChatMessage[];
}

/** A chat a teammate shared with the reader's organization, as its list
 *  shows it. */
export interface SharedChatSummary {
  id: string;
  title: string | null;
  owner_user_id: string;
  /** The agent whose chat it is, where it opens; null for a Clu chat. */
  context_agent_id?: string | null;
  shared_at: string | null;
  modified_at: string;
}

/** A shared chat's link, which opens it. */
export const sharedChatPath = (id: string) => `/clu/shared/${id}`;

export const cluSharingApi = createApi({
  reducerPath: 'cluSharingApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['SharedChat'],
  endpoints: (builder) => ({
    // Keyed by the active organization, so switching orgs reads its own list.
    getSharedWithMe: builder.query<SharedChatSummary[], string>({
      query: (orgId) => ({
        url: '/agent/shared-conversations',
        headers: { 'X-Cleanlist-Expected-Org': orgId },
      }),
      transformResponse: ({
        conversations,
      }: {
        conversations: SharedChatSummary[];
      }) => conversations,
    }),
    getSharedChat: builder.query<SharedCluChat, string>({
      query: (id) => `/agent/conversations/${id}/shared`,
      providesTags: (_result, _error, id) => [{ type: 'SharedChat', id }],
    }),
    setChatShared: builder.mutation<
      { id: string; shared_at: string | null },
      { id: string; shared: boolean }
    >({
      query: ({ id, shared }) => ({
        url: `/agent/conversations/${id}/share`,
        method: 'PUT',
        body: { shared },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'SharedChat', id },
      ],
    }),
    /** A copy of the chat's text as a new Clu chat of the caller's own. */
    forkChat: builder.mutation<{ id: string; title: string | null }, string>({
      query: (id) => ({
        url: `/agent/conversations/${id}/fork`,
        method: 'POST',
      }),
    }),
  }),
});

const NO_SHARED_CHATS: SharedChatSummary[] = [];

/**
 * The chats teammates shared with the reader's organization, newest share
 * first, where sharing is on; empty otherwise. Read again each minute while
 * the window has focus, and on coming back to it, so a new share shows up
 * without the link.
 */
export function useSharedWithMe(): SharedChatSummary[] {
  const enabled = useCluChatSharingEnabled();
  const { orgId } = useAuth();
  const { data } = useGetSharedWithMeQuery(
    enabled && orgId ? orgId : skipToken,
    {
      pollingInterval: 60_000,
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
    }
  );
  return (enabled && data) || NO_SHARED_CHATS;
}

export const {
  useGetSharedWithMeQuery,
  useGetSharedChatQuery,
  useSetChatSharedMutation,
  useForkChatMutation,
} = cluSharingApi;
